import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  fetchDisabledFeatures,
  fetchTableSettings,
  saveAppearance,
  isThemePreference,
  THEME_PREFERENCES,
} from '../api';

const THEME_STORAGE_KEY = 'qhs-theme-v2';

const checkSideMenu = () => {
  return localStorage.getItem('IsSideMenuPinned');
};

const setIsSideMenuPinned = val => {
  localStorage.setItem('IsSideMenuPinned', val);
};

// Theme model: the user's PREFERENCE ('light' | 'dark' | 'auto') and the
// EFFECTIVE theme ('light' | 'dark') that actually reaches <html
// data-theme>. 'auto' follows the OS via prefers-color-scheme and is
// re-resolved live by ThemeProvider whenever the OS switches.
//
// The backend (GET /user/preferences -> appearance.theme) is the
// cross-device source of truth and wins on load. localStorage keeps the
// last known preference purely so the first paint before that response
// does not flash the wrong theme.
const resolveSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? THEME_PREFERENCES.dark
      : THEME_PREFERENCES.light;
  }
  return THEME_PREFERENCES.light;
};

// No cached preference falls back to 'light' — the same default the
// backend merges in for a user who never chose (API 627012863), so the
// first paint and the first preferences response agree instead of
// flashing from the OS theme to light.
const getInitialThemePreference = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    // Older builds stored a bare 'light' | 'dark' under the same key;
    // both are still valid explicit preferences.
    if (isThemePreference(saved)) return saved;
  } catch (_) {
    // localStorage unavailable — fall through
  }
  return THEME_PREFERENCES.light;
};

const persistThemePreference = value => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch (_) {
    // ignore
  }
};

const effectiveTheme = (preference, systemTheme) =>
  preference === THEME_PREFERENCES.auto ? systemTheme : preference;

// Single write path for the preference so every caller (user action,
// backend load, optimistic save, rollback) keeps the three fields in
// step and the local cache current.
const applyThemePreference = (state, preference) => {
  if (!isThemePreference(preference)) return;
  state.themePreference = preference;
  state.theme = effectiveTheme(preference, state.systemTheme);
  persistThemePreference(preference);
};

const readAppearanceTheme = payload => payload?.data?.data?.preferences?.appearance?.theme;

export const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState: {
    isSideMenuPinned: true,
    disabledFeatures: [],
    status: REQUEST_STATUS.idle,
    error: null,

    // Idle session (features/session-idle): the "Session Timeout" dialog
    // during the last minute, and whether the user has been away for the
    // active window (no interaction for min(5 min, idle timeout)). The
    // journey poller pauses and the alerts hub disconnects while idle.
    isAutoLogoutDialogOpen: false,
    isUserIdle: false,

    //screenSize
    screenHeight: 1000,
    screenWidth: 600,

    // navigation scroll position
    navigationScrollPosition: 0,

    // See the theme model note above. `theme` is what <html data-theme>
    // gets; `themePreference` is what the user chose and what is
    // persisted; `systemTheme` is the OS value 'auto' resolves to.
    themePreference: getInitialThemePreference(),
    systemTheme: resolveSystemTheme(),
    theme: effectiveTheme(getInitialThemePreference(), resolveSystemTheme()),
  },
  reducers: {
    // Local-only change (no backend write) — used by the theme toggles
    // on the public pages and as the building block for everything else.
    setTheme: (state, action) => {
      applyThemePreference(state, action.payload);
    },
    // Toggling from 'auto' picks the explicit opposite of what is
    // currently shown, which is what a person pressing "switch" means.
    toggleTheme: state => {
      const next =
        state.theme === THEME_PREFERENCES.dark ? THEME_PREFERENCES.light : THEME_PREFERENCES.dark;
      applyThemePreference(state, next);
    },
    // Fired by ThemeProvider's prefers-color-scheme listener.
    setSystemTheme: (state, action) => {
      const value = action.payload;
      if (value !== THEME_PREFERENCES.light && value !== THEME_PREFERENCES.dark) return;
      state.systemTheme = value;
      state.theme = effectiveTheme(state.themePreference, value);
    },
    setScreenSize: (state, action) => {
      state.screenHeight = action.payload.height;
      state.screenWidth = action.payload.width;
    },
    getAndSetIsSideMenuPinned: state => {
      if (checkSideMenu() === 'false') {
        state.isSideMenuPinned = false;
      }
    },
    setAutoLogoutDialogOpen: (state, action) => {
      state.isAutoLogoutDialogOpen = action.payload;
    },
    setUserIdle: (state, action) => {
      state.isUserIdle = action.payload;
    },
    // Signal only (no state): a keepalive or "Stay logged in" succeeded,
    // so the JWT is fresh — the alerts middleware restarts a hub that
    // gave up reconnecting.
    sessionKeptAlive: state => state,
    toggleIsSideMenuPinned: state => {
      const newIsSideMenuPinned = !state.isSideMenuPinned;

      setIsSideMenuPinned(newIsSideMenuPinned);
      state.isSideMenuPinned = newIsSideMenuPinned;
    },
    setNavigationScrollPosition: (state, action) => {
      state.navigationScrollPosition = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDisabledFeatures.pending, state => {
        state.status = REQUEST_STATUS.loading;
      })
      .addCase(fetchDisabledFeatures.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error =
          action.payload?.error || action.error?.message || 'Failed to load disabled features';
      })
      .addCase(fetchDisabledFeatures.fulfilled, (state, action) => {
        state.status = REQUEST_STATUS.succeeded;
        state.disabledFeatures = action.payload.data.data;
        state.error = null;
      })
      // The preferences read the tables already make also carries the
      // theme; the backend value wins over the local cache. An absent or
      // malformed `appearance` leaves the local value alone — the
      // contract says it is always present, so its absence means an
      // older backend, not a user who chose nothing.
      .addCase(fetchTableSettings.fulfilled, (state, action) => {
        if (!action.payload?.ok) return;
        applyThemePreference(state, readAppearanceTheme(action.payload));
      })
      // Optimistic save: apply on request, roll back to `previous` on
      // failure, reconcile with the server's copy on success.
      .addCase(saveAppearance.pending, (state, action) => {
        applyThemePreference(state, action.meta.arg.theme);
      })
      .addCase(saveAppearance.rejected, (state, action) => {
        applyThemePreference(state, action.meta.arg.previous);
      })
      .addCase(saveAppearance.fulfilled, (state, action) => {
        applyThemePreference(state, readAppearanceTheme(action.payload));
      });
  },
});

export const {
  setScreenSize,
  getAndSetIsSideMenuPinned,
  toggleIsSideMenuPinned,
  setAutoLogoutDialogOpen,
  setUserIdle,
  sessionKeptAlive,
  setNavigationScrollPosition,
  setTheme,
  toggleTheme,
  setSystemTheme,
} = appSettingsSlice.actions;

export default appSettingsSlice.reducer;
